import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, MessageCircle, Facebook, Instagram, ArrowUpRight } from "lucide-react";
import { SITE, buildWhatsAppLink } from "../../lib/site";
import { LicensePlate } from "../LicensePlate";
import { MadeInStamp } from "../MadeInStamp";

const navLinks = [
  { href: "#top", label: "Acasă" },
  { href: "#cum-functioneaza", label: "Cum funcționează" },
  { href: "#galerie", label: "Galerie" },
  { href: "#testimoniale", label: "Testimoniale" },
  { href: "#intrebari", label: "Întrebări" },
  { href: "#contact", label: "Contact" },
];

const legalLinks = [
  { to: "/politica-confidentialitate", label: "Politica de Confidențialitate" },
  { to: "/politica-cookies", label: "Politica Cookies" },
  { to: "/termeni-si-conditii", label: "Termeni și Condiții" },
];

const linkClass =
  "group inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors duration-300";

const NavLink = ({ href, label }) => (
  <li>
    <a href={href} data-testid={`footer-nav-${href.slice(1) || "acasa"}`} className={linkClass}>
      <span className="w-0 h-px bg-[#3B82F6] transition-all duration-300 group-hover:w-5" />
      <span className="transition-transform duration-300 group-hover:translate-x-1">{label}</span>
    </a>
  </li>
);

const LegalLink = ({ to, label }) => (
  <li>
    <Link to={to} data-testid={`footer-legal-${to.slice(1)}`} className={linkClass}>
      <span className="w-0 h-px bg-[#3B82F6] transition-all duration-300 group-hover:w-5" />
      <span className="transition-transform duration-300 group-hover:translate-x-1">{label}</span>
    </Link>
  </li>
);

const ContactRow = ({ icon: Icon, label, value, href, testid }) => {
  const Wrap = href ? "a" : "div";
  return (
    <Wrap
      {...(href ? { href, target: href.startsWith("http") ? "_blank" : undefined, rel: "noopener noreferrer" } : {})}
      data-testid={testid}
      className="group flex items-start gap-3.5"
    >
      <span className="grid place-items-center shrink-0 w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-[#60A5FA] group-hover:bg-[#3B82F6] group-hover:text-white group-hover:border-transparent transition-all duration-300">
        <Icon size={17} />
      </span>
      <span className="pt-0.5">
        <span className="block text-xs uppercase tracking-wider text-slate-500">{label}</span>
        <span className="block text-[15px] text-slate-200 group-hover:text-white transition-colors">{value}</span>
      </span>
    </Wrap>
  );
};

export const Footer = () => (
  <footer data-testid="footer" className="relative overflow-hidden bg-[#0A1626] text-slate-300">
    {/* blue glow accents */}
    <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 bg-[#1565C0]/20 rounded-full blur-3xl" />
    <div className="pointer-events-none absolute -bottom-32 right-0 w-96 h-96 bg-[#0D47A1]/25 rounded-full blur-3xl" />
    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#1565C0]/60 to-transparent" />

    <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
      <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.3fr_0.8fr_1fr_1.2fr]">
        {/* Column 1 — brand */}
        <div>
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#1565C0] text-white font-display font-bold text-xl shadow-lg shadow-[#1565C0]/30">A</span>
            <span className="font-display font-bold text-xl text-white">
              Auto Valy <span className="text-[#60A5FA]">Drive</span>
            </span>
          </div>
          <p className="mt-5 text-slate-400 text-[15px] leading-relaxed max-w-xs">
            Pregătim viitorii șoferi din Pașcani cu seriozitate, răbdare și profesionalism.
          </p>

          <div className="mt-6">
            <LicensePlate />
            <p className="mt-2 text-xs text-slate-500">Mașina noastră de școală · Pașcani</p>
          </div>

          <div className="mt-6 flex items-center gap-4">
            <a href={SITE.social.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="grid place-items-center w-10 h-10 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:bg-[#3B82F6] hover:text-white hover:border-transparent hover:-translate-y-0.5 transition-all duration-300">
              <Facebook size={17} />
            </a>
            <a href={SITE.social.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="grid place-items-center w-10 h-10 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:bg-[#3B82F6] hover:text-white hover:border-transparent hover:-translate-y-0.5 transition-all duration-300">
              <Instagram size={17} />
            </a>
            <MadeInStamp className="ml-auto w-20 h-20 text-white/60 -rotate-6" />
          </div>
        </div>

        {/* Column 2 — quick nav */}
        <div>
          <h4 className="font-display font-semibold text-white text-sm uppercase tracking-[0.15em]">Navigare rapidă</h4>
          <ul className="mt-6 space-y-3.5 text-[15px]">
            {navLinks.map((l) => (
              <NavLink key={l.href} {...l} />
            ))}
          </ul>
        </div>

        {/* Column 3 — legal */}
        <div>
          <h4 className="font-display font-semibold text-white text-sm uppercase tracking-[0.15em]">Informații legale</h4>
          <ul className="mt-6 space-y-3.5 text-[15px]">
            {legalLinks.map((l) => (
              <LegalLink key={l.to} {...l} />
            ))}
          </ul>
        </div>

        {/* Column 4 — contact */}
        <div>
          <h4 className="font-display font-semibold text-white text-sm uppercase tracking-[0.15em]">Contact</h4>
          <div className="mt-6 space-y-4">
            <ContactRow icon={Phone} label="Telefon" value={SITE.phoneDisplay} href={`tel:${SITE.phoneRaw}`} testid="footer-phone" />
            <ContactRow icon={MessageCircle} label="WhatsApp" value="Scrie-ne pe WhatsApp" href={buildWhatsAppLink("Bună ziua! Doresc detalii despre categoria B.")} testid="footer-whatsapp" />
            <ContactRow icon={Clock} label="Program" value={SITE.schedule} testid="footer-schedule" />
            <ContactRow icon={MapPin} label="Locație" value={SITE.address} href={`https://maps.google.com/?q=${encodeURIComponent(SITE.address)}`} testid="footer-location" />
          </div>
        </div>
      </div>

      {/* separator */}
      <div className="mt-14 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

      {/* ANPC official badges */}
      <div data-testid="anpc-badges" className="pt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
        <a
          href="https://anpc.ro/ce-este-sal/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="anpc-sal-badge"
          aria-label="ANPC — Soluționarea Alternativă a Litigiilor"
          className="inline-block rounded-lg bg-white p-1.5 hover:-translate-y-0.5 transition-transform duration-300"
        >
          <img src="/anpc-sal.webp" alt="ANPC — Soluționarea Alternativă a Litigiilor" width="250" height="63" loading="lazy" className="h-12 sm:h-14 w-auto" />
        </a>
        <a
          href="https://ec.europa.eu/consumers/odr"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="anpc-sol-badge"
          aria-label="ANPC — Soluționarea Online a Litigiilor"
          className="inline-block rounded-lg bg-white p-1.5 hover:-translate-y-0.5 transition-transform duration-300"
        >
          <img src="/anpc-sol.webp" alt="ANPC — Soluționarea Online a Litigiilor" width="250" height="63" loading="lazy" className="h-12 sm:h-14 w-auto" />
        </a>
      </div>

      {/* copyright bar */}
      <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
        <p>© 2026 Auto Valy Drive • Toate drepturile rezervate</p>
        <p>
          Realizat de{" "}
          <a
            href="https://wa.me/40754482899"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-credit-link"
            className="group inline-flex items-center gap-0.5 font-medium text-slate-300 hover:text-[#60A5FA] transition-colors duration-300"
          >
            Alexandru
            <ArrowUpRight size={14} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
          </a>
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
